/**
 * useStepData Hook
 *
 * Manages step data viewer state: maps function_call_id to step_number
 * via the /api/sessions/{sessionId}/steps endpoint, and tracks which
 * step is currently selected for viewing.
 *
 * The panel opens on first selectStep() and stays open until closeStepData().
 * Subsequent selectStep() calls just swap the displayed step.
 */

import { useState, useCallback, useRef } from 'react';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface StepMapping {
  function_call_id: string;
  step_number: number;
}

export interface UseStepDataReturn {
  selectedStep: number | null;
  selectedSessionId: string | null;
  /** The functionCallId of the currently selected step (for highlighting in EventStream). */
  selectedFunctionCallId: string | null;
  /** True when the panel should be visible (open until explicitly closed). */
  isViewingStepData: boolean;
  /** True while the step mapping fetch is in flight (panel should show loading). */
  isLoadingStep: boolean;
  /** Select a step by functionCallId. Opens panel if closed, swaps step if open. */
  selectStep: (sessionId: string, functionCallId: string) => Promise<boolean>;
  /** Only way to close the data panel. */
  closeStepData: () => void;
}

export const useStepData = (): UseStepDataReturn => {
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedFunctionCallId, setSelectedFunctionCallId] = useState<string | null>(null);
  const [isLoadingStep, setIsLoadingStep] = useState(false);
  // Explicit open flag — only closeStepData() sets this to false.
  const [panelOpen, setPanelOpen] = useState(false);
  const cacheRef = useRef<Map<string, StepMapping[]>>(new Map());

  const fetchStepMappings = useCallback(async (sessionId: string): Promise<StepMapping[]> => {
    const cached = cacheRef.current.get(sessionId);
    if (cached) return cached;

    const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/steps`;
    const response = await authenticatedFetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch step mappings: HTTP ${response.status}`);
    }

    const json = await response.json();
    const mappings: StepMapping[] = json.steps || [];
    cacheRef.current.set(sessionId, mappings);
    return mappings;
  }, []);

  const selectStep = useCallback(
    async (sessionId: string, functionCallId: string): Promise<boolean> => {
      // Open panel immediately, show loading
      setSelectedSessionId(sessionId);
      setSelectedFunctionCallId(functionCallId);
      setPanelOpen(true);
      setIsLoadingStep(true);
      try {
        let mappings = await fetchStepMappings(sessionId);
        let match = mappings.find((m) => m.function_call_id === functionCallId);

        // Cache miss — step was added after last fetch. Invalidate and retry.
        if (!match) {
          cacheRef.current.delete(sessionId);
          mappings = await fetchStepMappings(sessionId);
          match = mappings.find((m) => m.function_call_id === functionCallId);
        }

        if (match) {
          setSelectedStep(match.step_number);
          setIsLoadingStep(false);
          return true;
        }
        // Step genuinely not found
        setSelectedStep(null);
        setIsLoadingStep(false);
        return false;
      } catch {
        // Fetch failed — keep panel open but clear step number
        setSelectedStep(null);
        setIsLoadingStep(false);
        return false;
      }
    },
    [fetchStepMappings]
  );

  const closeStepData = useCallback(() => {
    setPanelOpen(false);
    setSelectedStep(null);
    setSelectedSessionId(null);
    setSelectedFunctionCallId(null);
  }, []);

  return {
    selectedStep,
    selectedSessionId,
    selectedFunctionCallId,
    isViewingStepData: panelOpen,
    isLoadingStep,
    selectStep,
    closeStepData,
  };
};
